import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { saveSvgAsPng } from 'save-svg-as-png';
import swal from 'sweetalert';
import ColoredInImage from '../Images/ColoredInImage';
import LoadingSpinner from '../LoadingSpinner';

// Material-UI Imports
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

// Material-UI Icons
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import PaletteIcon from '@mui/icons-material/Palette';
import CollectionsIcon from '@mui/icons-material/Collections';
// Removed AddShoppingCartIcon as it's no longer needed
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Styles for Material-UI components
const useStyles = makeStyles((theme) => ({
    button: {
        margin: theme.spacing(1),
    },
}));

export default function MyFeaturedImage() {
    let { id } = useParams(); // Get the image ID from the URL parameters
    const { token } = useSelector(state => state.auth); // Get authentication token from Redux store
    const [userImage, setUserImage] = useState(null); // State to store the fetched user image data
    const [loading, setLoading] = useState(true); // State to manage loading status
    const [error, setError] = useState(null); // State to store any fetch errors
    const history = useHistory(); // For programmatic navigation
    const classes = useStyles(); // Apply Material-UI styles

    // Effect hook to fetch user image data when component mounts or ID/token changes
    useEffect(() => {
        if (!token) {
            history.push("/login"); // Redirect to login if no token is found
            return;
        }

        setLoading(true); // Set loading to true before fetch
        setError(null); // Clear any previous errors

        fetch(`https://test-api.stainedglassusa.com/user_images/${id}`, {
            headers: { 'Authorization': `bearer ${token}` } // Send authorization token
        })
            .then(r => {
                if (!r.ok) {
                    // If response is not OK (e.g., 404, 500), throw an error
                    throw new Error(`Failed to fetch image data: ${r.statusText}`);
                }
                return r.json(); // Parse JSON response
            })
            .then(data => {
                setUserImage(data); // Set fetched data to state
                setLoading(false); // Set loading to false
            })
            .catch(err => {
                console.error("Error fetching user image:", err);
                setError(err.message); // Set error message
                setLoading(false); // Set loading to false even on error
            });
    }, [id, token, history]); // Dependencies for useEffect: re-run if id, token, or history changes

    // Derived states based on the 'userImage' data, using current_composite_status
    // current_composite_status is provided by the UserImageSerializer
    const currentCompositeStatus = userImage?.current_composite_status;

    // True if the image is uncolored (all fill_colors are white/default)
    const isUncolored = userImage?.fill_colors?.every(color => color === "white");

    // True if a quote has been submitted (status is "quote")
    const isQuoteSubmitted = currentCompositeStatus === "quote";

    // True if an order has been placed (status is pending, completed, or cancelled)
    const isOrdered = ["pending", "completed", "cancelled"].includes(currentCompositeStatus);

    // True if the image can be edited (it's uncolored or colored but not yet quoted/ordered)
    const canEdit = currentCompositeStatus === "coloring" || currentCompositeStatus === "available_for_quote_or_order";

    // True if the image can be deleted (it's uncolored or colored but not yet quoted/ordered)
    const canDelete = currentCompositeStatus === "coloring" || currentCompositeStatus === "available_for_quote_or_order";

    // Removed canAddToCart as the functionality is no longer needed


    // Display loading spinner while data is being fetched
    if (loading) {
        return <LoadingSpinner />;
    }

    // Display error message if fetch failed
    if (error) {
        return <h1 style={{ color: 'red', textAlign: 'center' }}>Error: {error}</h1>;
    }

    // Display message if image data is not found or accessible after loading
    if (!userImage || !userImage.id || !userImage.image?.component) {
        return (
            <h1 style={{ textAlign: 'center' }}>Image not found or not accessible.</h1>
        );
    }

    // Handler for deleting an image
    const deleteImage = () => {
        swal({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to recover this image",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        })
            .then((willDelete) => {
                if (willDelete) {
                    fetch(`https://test-api.stainedglassusa.com/user_images/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `bearer ${token}` }
                    })
                        .then(r => {
                            if (!r.ok) {
                                throw new Error('Failed to delete image.');
                            }
                            return r.json();
                        })
                        .then(() => {
                            swal("Image Deleted", "This image has been removed from your gallery", "success");
                            history.push(`/mygallery`); // Navigate back to gallery after deletion
                        })
                        .catch(err => {
                            console.error("Error deleting image:", err);
                            swal("Error", `Failed to delete image: ${err.message}`, "error");
                        });
                }
            });
    };

    // Removed addToCart function as it's no longer needed

    // Handler for editing an image (navigates to coloring page)
    const editImage = () => {
        history.push(`/coloringpage/${userImage.id}`);
    };

    // Handler for navigating back to the user's gallery
    const backToGallery = () => {
        history.push('/mygallery');
    };

    // Handler for saving the SVG as a PNG
    const saveImage = () => {
        saveSvgAsPng(document.getElementById("svg-image"), `stainedglasswindows_${userImage.id}.png`);
        swal("Download Complete", "This image has been saved to your Downloads", "success");
    };

    return (
        <div className="featured-image-page">
            <div className="featured-image-container" style={{ pointerEvents: 'none' }}>
                <ColoredInImage
                    size={'large'}
                    component={userImage.image?.component} // Access component via userImage.image
                    fillColors={userImage.fill_colors}
                    style={{ pointerEvents: 'none' }} // Prevents interaction with the image itself
                    id="svg-image" // Add an ID for saveSvgAsPng
                />
                <h4 style={{ textAlign: "center" }}>Reference Image: {userImage.id}</h4>
            </div>

            <div id="featured-btn">
                {/* Display current status */}
                <p style={{ textAlign: 'center', margin: '16px 0', color: '#555', fontWeight: 'bold' }}>
                    Current Status: {currentCompositeStatus || "Quote submitted. We will contact you soon."}
                </p>

                {/* Edit Button */}
                <Tooltip title={canEdit ? "Edit this Design" : "Cannot edit: Quote or Order in progress"}>
                    <Button
                        variant="contained"
                        color="default"
                        className={classes.button}
                        startIcon={<PaletteIcon />}
                        onClick={editImage}
                        disabled={!canEdit} // Disable if not editable
                    >
                        Edit this Design
                    </Button>
                </Tooltip>

                {/* Download Button */}
                <Tooltip title="Download">
                    <Button
                        variant="contained"
                        color="default"
                        className={classes.button}
                        startIcon={<DownloadForOfflineIcon />}
                        onClick={saveImage}
                    >
                        Download
                    </Button>
                </Tooltip>

                {/* Back to Gallery Button */}
                <Tooltip title="Back To Gallery">
                    <Button
                        variant="contained"
                        color="default"
                        className={classes.button}
                        startIcon={<CollectionsIcon />}
                        onClick={backToGallery}
                    >
                        Back to Gallery
                    </Button>
                </Tooltip>

                {/* Removed Add to Cart Button and its Tooltip */}

                {/* Delete Button */}
                <Tooltip title={canDelete ? "Delete" : "Cannot delete: Quote or Order in progress"}>
                    <Button
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        startIcon={<DeleteForeverIcon />}
                        onClick={deleteImage}
                        disabled={!canDelete} // Disable if not deletable
                    >
                        Delete
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
}
